import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { CoachCatalogService } from './coach-catalog.service';
import { IPeriod } from '@gray/domain/models/exam/exam-period.model';

const STORAGE_KEY = 'coach_preferred_period';

/**
 * Which period the students screen is reading. A student's level, department and scores are per
 * period, so the choice travels as a `periodId` query parameter on the screens that need it — never
 * as a header — and survives a reload. Without a stored choice the active period is selected.
 */
@Injectable({providedIn: 'root'})
export class CoachPeriodService {
  private readonly periodsSubject = new BehaviorSubject<IPeriod[]>([]);
  private readonly selectedIdSubject = new BehaviorSubject<number | null>(this.readStoredId());

  readonly periods$: Observable<IPeriod[]> = this.periodsSubject.asObservable();
  readonly selectedId$: Observable<number | null> = this.selectedIdSubject.asObservable();

  constructor(
    private catalogService: CoachCatalogService,
  ) {
  }

  get periods(): IPeriod[] {
    return this.periodsSubject.value;
  }

  get selectedId(): number | null {
    return this.selectedIdSubject.value;
  }

  get selectedPeriod(): IPeriod | null {
    return this.periods.find(period => period.id === this.selectedId) ?? null;
  }

  /** Loads the selectable periods and falls back to the active one when nothing is stored. */
  loadPeriods(): Observable<any> {
    return this.catalogService.getPeriods()
      .pipe(tap(r => {
        const periods = (r.data?.result ?? r.data ?? []) as IPeriod[];
        this.periodsSubject.next(periods);

        const stored = this.selectedId;
        const isKnown = stored && periods.some(period => period.id === stored);
        if (!isKnown) {
          this.select(periods.find(period => period.isActive)?.id ?? periods[0]?.id ?? null);
        }
      }));
  }

  select(periodId: number | null) {
    if (periodId === this.selectedId) {
      return;
    }

    this.selectedIdSubject.next(periodId);
    this.storeId(periodId);
  }

  private readStoredId(): number | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private storeId(periodId: number | null) {
    if (periodId) {
      localStorage.setItem(STORAGE_KEY, `${periodId}`);
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
  }
}
